import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  ArrowDown, ArrowUp, ArrowsLeftRight, ArrowRight, CheckCircle, Clock, Warning, Trash,
  CaretLeft, CaretRight, TrendUp, TrendDown, Wallet, Sparkle,
} from "@phosphor-icons/react";
import { api, brl, getUser, saveAuth } from "@/lib/api";

const StatusPill = ({ s }) => {
  const map = {
    approved: { c: "var(--success)", t: "Aprovado", I: CheckCircle },
    pending:  { c: "var(--accent)",  t: "Pendente", I: Clock },
    failed:   { c: "var(--danger)",  t: "Falha",    I: Warning },
    rejected: { c: "var(--danger)",  t: "Rejeitado",I: Warning },
  };
  const m = map[s] || map.pending;
  const I = m.I;
  return (
    <span className="inline-flex items-center gap-1 text-xs" style={{ color: m.c }}>
      <I size={12} weight="fill" /> {m.t}
    </span>
  );
};

const QUICK_ACTIONS = [
  {
    to: "/app/deposit",
    Icon: ArrowDown,
    label: "Depositar",
    sub: "Gere um QR Code Pix na hora",
    color: "#10b981",
    testid: "quick-deposit",
  },
  {
    to: "/app/transfer",
    Icon: ArrowsLeftRight,
    label: "Transferir",
    sub: "Envie Pix para qualquer conta MercadoPagu",
    color: "var(--accent)",
    testid: "quick-transfer",
  },
  {
    to: "/app/withdraw",
    Icon: ArrowUp,
    label: "Sacar",
    sub: "Envie para sua chave Pix externa",
    color: "#60a5fa",
    testid: "quick-withdraw",
  },
];

export default function Dashboard() {
  const [balance, setBalance] = useState(0);
  const [totals, setTotals] = useState({ total_in: 0, total_out: 0 });
  const [page, setPage] = useState(1);
  const [pageData, setPageData] = useState({ transactions: [], total: 0, pages: 1 });
  const [canDelete, setCanDelete] = useState(false);
  const user = getUser();

  const loadSummary = async () => {
    const { data: r } = await api.get("/account/summary");
    setBalance(r.balance);
    setTotals({ total_in: r.total_in, total_out: r.total_out });
    try {
      const me = await api.get("/auth/me");
      saveAuth(localStorage.getItem("vb_token"), { ...user, ...me.data });
    } catch (e) { console.warn("me failed:", e?.message || e); }
  };
  const loadPage = async (p = 1) => {
    const { data } = await api.get(`/account/transactions?page=${p}&per_page=10`);
    setPageData(data);
    setPage(p);
  };
  useEffect(() => {
    loadSummary();
    loadPage(1);
    api.get("/settings/public").then(({ data }) => setCanDelete(!!data.allow_client_delete)).catch(() => {});
    /* eslint-disable-next-line */
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Ocultar esta transação do seu extrato?")) return;
    await api.delete(`/account/transactions/${id}`);
    toast.success("Transação removida do extrato");
    loadPage(page);
  };

  const firstName = (user?.name || "").split(" ")[0] || "por aí";
  const hour = new Date().getHours();
  const greeting =
    hour < 6 ? "Boa madrugada" :
    hour < 12 ? "Bom dia" :
    hour < 18 ? "Boa tarde" : "Boa noite";

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
    >
      {/* HERO SALDO */}
      <div className="relative mb-8 md:mb-10 overflow-hidden rounded-3xl border border-white/[0.06]" data-testid="dashboard-hero">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at 20% 20%, rgba(255,179,15,0.18), transparent 55%), radial-gradient(ellipse at 80% 80%, rgba(255,179,15,0.06), transparent 60%)",
          }}
        />
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none opacity-[0.03]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
        <div className="relative p-6 sm:p-8 md:p-10 grid md:grid-cols-2 gap-6 md:gap-8 items-center">
          <div>
            <div className="label-caps mb-2 inline-flex items-center gap-1.5">
              <Sparkle size={11} weight="fill" className="text-[var(--accent)]" />
              {greeting}, {firstName}
            </div>
            <h1 className="page-title wrap-safe mb-1.5">Sua conta digital</h1>
            <p className="page-subtitle wrap-safe">
              Movimente com Pix ao vivo, receba de qualquer canto do Brasil.
            </p>
            <div className="flex flex-wrap gap-2 mt-6">
              <Link to="/app/deposit" className="btn-primary" data-testid="hero-cta-deposit">
                <ArrowDown size={16} weight="bold" /> Depositar
              </Link>
              <Link to="/app/transfer" className="btn-secondary" data-testid="hero-cta-transfer">
                <ArrowsLeftRight size={16} weight="bold" /> Transferir
              </Link>
              <Link to="/app/withdraw" className="btn-secondary" data-testid="hero-cta-withdraw">
                <ArrowUp size={16} weight="bold" /> Sacar
              </Link>
            </div>
          </div>
          <div className="md:text-right">
            <div className="label-caps mb-2 flex items-center gap-1.5 md:justify-end">
              <Wallet size={11} weight="fill" className="text-[var(--accent)]" />
              Saldo disponível
            </div>
            <motion.div
              key={balance}
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="font-display font-black text-[var(--accent)] tracking-tighter tabular-nums leading-none wrap-safe"
              style={{ fontSize: "clamp(2rem, 6vw + 0.5rem, 4.5rem)" }}
              data-testid="dashboard-balance"
            >
              {brl(balance)}
            </motion.div>
            <div className="flex md:justify-end gap-4 mt-4 text-xs">
              <div className="flex items-center gap-1.5 text-[var(--success)]">
                <TrendUp size={12} weight="fill" /> entradas
                <span className="font-mono font-semibold tabular-nums" data-testid="stat-in">{brl(totals.total_in)}</span>
              </div>
              <div className="flex items-center gap-1.5 text-white/50">
                <TrendDown size={12} weight="fill" /> saídas
                <span className="font-mono font-semibold tabular-nums" data-testid="stat-out">{brl(totals.total_out)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* AÇÕES RÁPIDAS */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 mb-8 md:mb-10">
        {QUICK_ACTIONS.map((a, i) => (
          <motion.div
            key={a.to}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 + i * 0.08, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          >
            <Link to={a.to} className="app-card block group relative overflow-hidden" data-testid={a.testid}>
              <div
                aria-hidden
                className="absolute -top-8 -right-8 w-24 h-24 rounded-full opacity-30 group-hover:opacity-60 group-hover:scale-125 transition-all duration-500"
                style={{ background: `radial-gradient(circle, ${a.color}, transparent 70%)`, filter: "blur(20px)" }}
              />
              <div className="relative">
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110"
                  style={{ background: `${a.color}18`, border: `1px solid ${a.color}44` }}
                >
                  <a.Icon size={20} weight="duotone" style={{ color: a.color }} />
                </div>
                <div className="font-display font-bold text-lg wrap-safe">{a.label}</div>
                <div className="text-sm text-white/55 mt-1 wrap-safe leading-relaxed">{a.sub}</div>
                <div className="mt-4 flex items-center gap-1 text-xs font-mono text-white/40 group-hover:text-[var(--accent)] transition-colors">
                  abrir <ArrowRight size={12} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>

      {/* EXTRATO */}
      <div className="app-card p-0 overflow-hidden">
        <div className="p-5 md:p-6 border-b border-white/[0.06] flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="label-caps">Extrato</div>
            <div className="font-display font-bold text-lg mt-1 wrap-safe">
              Página {page} de {pageData.pages || 1} · <span className="text-white/50 font-mono text-sm">{pageData.total} movimentações</span>
            </div>
          </div>
        </div>
        <div className="divide-y divide-white/[0.04]">
          {pageData.transactions.length === 0 && (
            <div className="p-8 text-center" data-testid="no-tx">
              <div className="w-14 h-14 rounded-full bg-white/[0.03] border border-white/[0.06] flex items-center justify-center mx-auto mb-4">
                <Wallet size={22} className="text-white/30" />
              </div>
              <div className="text-white/60 text-sm mb-4">Ainda não tem movimentações.</div>
              <Link to="/app/deposit" className="btn-primary inline-flex" data-testid="empty-deposit-btn">
                <ArrowDown size={14} weight="bold" /> Fazer meu primeiro depósito
              </Link>
            </div>
          )}
          {pageData.transactions.map((t) => {
            const isIn = t.to_user === user?.id;
            return (
              <div
                key={t.id}
                className="p-4 md:p-5 flex items-center justify-between hover:bg-white/[0.015] transition-colors gap-3"
                data-testid={`tx-${t.id}`}
              >
                <div className="flex items-center gap-3 md:gap-4 min-w-0 flex-1">
                  <div
                    className="w-10 h-10 rounded-xl border flex items-center justify-center shrink-0"
                    style={{
                      color: isIn ? "var(--success)" : "var(--danger)",
                      background: isIn ? "rgba(16,185,129,0.08)" : "rgba(239,68,68,0.08)",
                      borderColor: isIn ? "rgba(16,185,129,0.25)" : "rgba(239,68,68,0.25)",
                    }}
                  >
                    {t.type === "transfer" ? <ArrowsLeftRight size={16} /> : (t.type === "deposit" || isIn) ? <ArrowDown size={16} /> : <ArrowUp size={16} />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm truncate">
                      {t.type === "deposit"
                        ? "Depósito Pix"
                        : t.type === "transfer"
                        ? (isIn ? `Recebido de ${t.from_email}` : `Enviado para ${t.to_email}`)
                        : (t.type === "funnel_sale" || t.type === "video_sale")
                        ? `Venda · ${t.plan_name || "Produto"}${t.payment_method === "card" ? " (cartão)" : ""}`
                        : `Saque Pix · ${t.beneficiary_name || ""}`}
                    </div>
                    <div className="text-xs text-white/50 flex flex-wrap gap-2 items-center mt-1">
                      <StatusPill s={t.status} />
                      <span className="text-white/30">·</span>
                      <span className="font-mono">{new Date(t.created_at).toLocaleString("pt-BR")}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <div className={`font-display font-bold text-sm md:text-base tabular-nums ${isIn ? "text-[var(--success)]" : "text-white/80"}`}>
                    {isIn ? "+" : "-"} {brl(t.amount)}
                  </div>
                  {(canDelete || user?.role === "admin") && (
                    <button
                      onClick={() => remove(t.id)}
                      data-testid={`del-tx-${t.id}`}
                      className="text-white/30 hover:text-[var(--danger)] transition p-2"
                    >
                      <Trash size={14} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {pageData.pages > 1 && (
          <div className="p-4 border-t border-white/[0.06] flex items-center justify-between gap-2">
            <button
              onClick={() => loadPage(Math.max(1, page - 1))}
              disabled={page <= 1}
              data-testid="prev-page"
              className="btn-secondary text-xs disabled:opacity-30"
            >
              <CaretLeft size={13} /> <span className="hidden sm:inline">Anterior</span>
            </button>
            <div className="flex gap-1.5">
              {Array.from({ length: pageData.pages }, (_, i) => i + 1)
                .slice(Math.max(0, page - 3), page + 2)
                .map((p) => (
                  <button
                    key={p}
                    onClick={() => loadPage(p)}
                    data-testid={`page-${p}`}
                    className={`w-9 h-9 rounded-lg text-xs font-mono font-semibold transition-colors ${
                      page === p
                        ? "bg-[var(--accent)] text-black"
                        : "border border-white/10 text-white/60 hover:text-white hover:border-white/20"
                    }`}
                  >
                    {p}
                  </button>
                ))}
            </div>
            <button
              onClick={() => loadPage(Math.min(pageData.pages, page + 1))}
              disabled={page >= pageData.pages}
              data-testid="next-page"
              className="btn-secondary text-xs disabled:opacity-30"
            >
              <span className="hidden sm:inline">Próxima</span> <CaretRight size={13} />
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
}
